import { BaseDialog, Button, Tabs } from '@app/components';
import { OptionInterface } from '@app/models/util';
import { GenericTable } from '@app/components/GenericTable/GenericTable';
import { CampaignsTableRow } from '@app/components/CampaignsTableRow/CampaignsTableRow';
import { CampaignsFilters } from '@app/components/CampaignsFilters/CampaignsFilters';
import { useCampaigns } from './Campaigns.hooks';
import CloseIcon from '@app/assets/icons/close.svg?react';
import CampaignsIcon from '@app/assets/icons/schedule-campaign.svg?react';
import CampaignScheduleIcon from '@app/assets/icons/menu/campaigns.svg?react';
import AutomationIcon from '@app/assets/icons/automation.svg?react';
import PlaneIcon from '@app/assets/icons/plane.svg?react';
import BinIcon from '@app/assets/icons/bin.svg?react';
import CalendarIcon from '@app/assets/icons/calendar-01.svg?react';
import CalendarCheckIcon from '@app/assets/icons/calendar-check.svg?react';
import { deleteCampaign, getCampaign } from '@app/services/campaigns';
import { toast } from 'react-toastify';
import NoCampaignIllustration from '@app/assets/illustrations/no-campaigns.svg?react';
import NoScheduledIllustration from '@app/assets/illustrations/no-scheduled.png';
import NoCompletedIllustration from '@app/assets/illustrations/no-completed.png';
import NoCancelIllustration from '@app/assets/illustrations/no-cancel.png';
import NoDraftsIllustration from '@app/assets/illustrations/no-drafts.png';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@app/contexts/PermissionContext';
import { useAppDispatch } from '@app/redux/hooks';
import { updateFormData } from '@app/redux/features/campaignForm';
import { formatDateToISO } from '@app/utils/utils';
import { reviewTemplate } from '@app/components/EmailTemplateEditor/templates';
import dayjs from 'dayjs';

export const Campaigns = () => {
  const {
    createSecheduleModal,
    handleCreateScheduleModal,
    campaignsData,
    createAutomation,
    createCampaign,
    handleDeleteModal,
    deleteModal,
    refetch,
    filterForm,
    handleLimitChange,
    handleNextPage,
    handlePreviousPage,
    page,
    limit,
    searchValue,
    setSearchValue,
    sortBy,
    sortOrder,
    handleSortingTable,
  } = useCampaigns();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { can } = usePermissions();
  const [highlightedCampaignId, setHighlightedCampaignId] = useState<
    string | null
  >(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [currentFilterStatus, setCurrentFilterStatus] = useState<string>('all');

  const campaignsTabs = [
    { value: 'all', label: 'All', count: campaignsData?.statusCounts?.total },
    {
      value: 'scheduled',
      label: 'Scheduled',
      count: campaignsData?.statusCounts?.scheduled,
    },
    {
      value: 'active',
      label: 'Active',
      count: campaignsData?.statusCounts?.active,
    },
    {
      value: 'completed',
      label: 'Completed',
      count: campaignsData?.statusCounts?.completed,
    },
    {
      value: 'cancelled',
      label: 'Cancelled',
      count: campaignsData?.statusCounts?.cancelled,
    },
    {
      value: 'draft',
      label: 'Drafts',
      count: campaignsData?.statusCounts?.draft,
    },
  ];

  const getScheduleSummary = (campaign: any) => {
    if (!campaign.schedules || campaign.schedules.length === 0) {
      return null;
    }

    const now = dayjs();
    const pastSchedules = campaign.schedules.filter((schedule: any) =>
      dayjs(schedule.start).isBefore(now)
    );
    const futureSchedules = campaign.schedules.filter((schedule: any) =>
      dayjs(schedule.start).isAfter(now)
    );

    return {
      pastCount: pastSchedules.length,
      futureCount: futureSchedules.length,
      pastSchedules,
      futureSchedules,
    };
  };

  const deleteCampaignAction = async () => {
    if (!deleteModal) return;

    await deleteCampaign(deleteModal.id)
      .then(async () => {
        await refetch();
        toast.success('Campaign deleted successfully');
      })
      .catch(() => {
        toast.error('Failed to delete campaign');
      })
      .finally(() => handleDeleteModal(undefined));
  };

  const handleTabClick = (tabValue: string) => {
    // Set filtering state
    setIsFiltering(true);
    setCurrentFilterStatus(tabValue);

    // Reset page to 1 when filtering by changing limit (which resets page)
    handleLimitChange(limit);

    if (tabValue === 'all') {
      // Clear status filter for "all" tab
      filterForm.setValue('statuses', []);
      filterForm.setValue('scheduled', false);
    } else if (tabValue === 'scheduled') {
      // Set scheduled filter for "scheduled" tab
      filterForm.setValue('statuses', ['scheduled']);
      filterForm.setValue('scheduled', true);
    } else {
      // Set status filter for other tabs
      filterForm.setValue('statuses', [tabValue]);
      filterForm.setValue('scheduled', false);
    }
  };

  const handleHighlightCampaign = (campaignId: string) => {
    // Clear any existing timeout
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    setHighlightedCampaignId(campaignId);

    // Store the highlighted campaign ID in sessionStorage to persist through re-renders
    sessionStorage.setItem('highlightedCampaignId', campaignId);
    sessionStorage.setItem('highlightStartTime', Date.now().toString());

    // Remove highlight after 8 seconds
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedCampaignId(null);
      sessionStorage.removeItem('highlightedCampaignId');
      sessionStorage.removeItem('highlightStartTime');
    }, 8000);
  };

  const handleCampaignClick = async (campaignId: string, status: string) => {
    // Check for draft status (case-insensitive and handle variations)
    const isDraftStatus =
      status?.toLowerCase() === 'drafts' ||
      status?.toLowerCase() === 'draft' ||
      status?.toLowerCase() === 'draft_status';

    if (isDraftStatus) {
      try {
        // Fetch the campaign data
        const response = await getCampaign(campaignId);
        const campaignData = response.data.data || response.data;

        // Transform campaign data to form format
        const formData = {
          campaignId: campaignData.id,
          campaignName: campaignData.name,
          description: campaignData.description,
          platforms: campaignData.platforms?.split(',') || [],
          businessName: campaignData.senderName,
          businessAddress: campaignData.senderAddress,
          businessEmail: campaignData.senderEmail,
          // Add other fields that might be available
          selectedCustomers:
            campaignData.customers?.map((customer: any) => customer.id) || [],
          // Template data
          emailTemplate:
            campaignData.template?.body ||
            campaignData.template?.emailTemplate ||
            campaignData.emailTemplate ||
            reviewTemplate,
          textTemplate:
            campaignData.template?.textTemplate ||
            campaignData.textTemplate ||
            'sms',
          // Schedule data - determine sendImmediately based on schedule existence
          sendImmediately:
            !campaignData.schedules || campaignData.schedules.length === 0,
          initialSendDate:
            !campaignData.schedules || campaignData.schedules.length === 0
              ? formatDateToISO(new Date()) // Current date if no schedule (send immediately)
              : campaignData.schedules[0]?.start
                ? formatDateToISO(new Date(campaignData.schedules[0].start))
                : formatDateToISO(new Date()),
          // Follow-up data if available
          sendFollowUp: campaignData.sendFollowUp || false,
          // Add any other fields that exist in the campaign data
        };

        // Save to Redux and localStorage
        dispatch(updateFormData(formData));
        localStorage.setItem('campaignFormData', JSON.stringify(formData));

        // Navigate to create campaign settings
        navigate('/campaigns/create/settings');
      } catch (error) {
        console.error('Error fetching campaign data:', error);
        // Fallback to normal navigation
        navigate(`/campaigns/${campaignId}`);
      }
    } else {
      // Normal navigation for non-draft campaigns
      navigate(`/campaigns/${campaignId}`);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('schedule')) {
      handleCreateScheduleModal();
    }
    if (urlParams.has('highlight')) {
      const highlightId = urlParams.get('highlight');
      if (highlightId) {
        handleHighlightCampaign(highlightId);
        // Clear the URL parameter after handling it
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('highlight');
        window.history.replaceState({}, '', newUrl.toString());
      }
    }
  }, []);

  // Restore highlighting from sessionStorage when component mounts or data changes
  useEffect(() => {
    const storedHighlightId = sessionStorage.getItem('highlightedCampaignId');
    const storedStartTime = sessionStorage.getItem('highlightStartTime');

    if (storedHighlightId && storedStartTime) {
      const startTime = parseInt(storedStartTime);
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(8000 - elapsed, 0);

      if (remainingTime > 0) {
        setHighlightedCampaignId(storedHighlightId);

        // Set timeout for remaining time
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedCampaignId(null);
          sessionStorage.removeItem('highlightedCampaignId');
          sessionStorage.removeItem('highlightStartTime');
        }, remainingTime);
      } else {
        // Clear expired highlighting
        sessionStorage.removeItem('highlightedCampaignId');
        sessionStorage.removeItem('highlightStartTime');
      }
    }
  }, [campaignsData]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // Track if filters have been applied
  useEffect(() => {
    const formValues = filterForm.getValues();
    const hasFilters =
      (formValues.statuses &&
        Array.isArray(formValues.statuses) &&
        formValues.statuses.length > 0) ||
      (formValues.statuses &&
        !Array.isArray(formValues.statuses) &&
        formValues.statuses !== 'all') ||
      formValues.scheduled ||
      (formValues.channels && formValues.channels !== 'all') ||
      (formValues.from && formValues.from !== '') ||
      (formValues.to && formValues.to !== '') ||
      searchValue.length > 0;

    setHasAppliedFilters(hasFilters);

    // Only update currentFilterStatus if it's a simple scheduled filter or no filters
    // Don't change the tab when complex filters are applied through filter fields
    if (formValues.scheduled && !hasOtherFilters(formValues)) {
      setCurrentFilterStatus('scheduled');
      setIsFiltering(true);
    } else if (!hasFilters) {
      // Reset filtering state when no filters are applied
      setIsFiltering(false);
      setCurrentFilterStatus('all');
    } else {
      // For complex filters, just set filtering to true but don't change the tab
      setIsFiltering(true);
    }
  }, [filterForm.watch(), searchValue]);

  // Helper function to check if there are other filters besides scheduled
  const hasOtherFilters = (formValues: any) => {
    // Check if there are complex filters (not just simple status filters)
    const hasComplexStatusFilters =
      formValues.statuses &&
      Array.isArray(formValues.statuses) &&
      formValues.statuses.length > 1; // More than one status filter

    const hasNonStatusFilters =
      (formValues.channels && formValues.channels !== 'all') ||
      (formValues.from && formValues.from !== '') ||
      (formValues.to && formValues.to !== '') ||
      searchValue.length > 0;

    return hasComplexStatusFilters || hasNonStatusFilters;
  };

  // Empty state configuration for different campaign statuses
  const getEmptyStateConfig = () => {
    // If there are complex filters applied, show generic message
    const formValues = filterForm.getValues();
    const hasComplexFilters = hasOtherFilters(formValues);

    if (hasComplexFilters) {
      return {
        image: NoCampaignIllustration,
        header: 'No campaigns found',
        description: 'No campaigns match your current filters',
      };
    }

    switch (currentFilterStatus) {
      case 'scheduled':
        return {
          image: NoScheduledIllustration,
          header: 'No scheduled campaigns',
          description: 'Scheduled campaigns will be displayed here',
        };
      case 'active':
        return {
          image: NoCampaignIllustration,
          header: 'No active campaigns',
          description:
            'Campaigns already sent but not yet completed will be displayed here',
        };
      case 'completed':
        return {
          image: NoCompletedIllustration,
          header: 'No completed campaigns',
          description:
            'Campaigns that have been fully sent and completed will be displayed here',
        };
      case 'cancelled':
        return {
          image: NoCancelIllustration,
          header: 'No cancelled campaigns',
          description: 'Cancelled campaigns will be displayed here',
        };
      case 'draft':
        return {
          image: NoDraftsIllustration,
          header: 'No drafts',
          description:
            'Campaigns that have not been fully created will be displayed here',
        };
      default:
        return {
          image: NoCampaignIllustration,
          header: 'No campaigns found',
          description: 'No campaigns match your current filters',
        };
    }
  };

  return (
    <>
      <div>
        <div className="bg-white border-b border-b-base-ghostWhite ">
          <div className="container mx-auto pt-4 px-4 space-y-2">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <h2 className="font-bold text-[28px] mb-2">Campaigns</h2>
              {can('createCampaigns') && (
                <Button
                  onClick={handleCreateScheduleModal}
                  leftNode={<CampaignsIcon className="[&_path]:stroke-white" />}
                >
                  Schedule new campaign
                </Button>
              )}
            </div>
            {((campaignsData?.total ?? 0) > 0 ||
              hasAppliedFilters ||
              isFiltering) && (
              <>
                <CampaignsFilters
                  filterForm={filterForm}
                  showReset={(() => {
                    const formValues = filterForm.getValues();
                    const hasActiveFilters =
                      (formValues.statuses &&
                        Array.isArray(formValues.statuses) &&
                        formValues.statuses.length > 0) ||
                      (formValues.channels &&
                        Array.isArray(formValues.channels) &&
                        formValues.channels.length > 0) ||
                      (formValues.platforms &&
                        Array.isArray(formValues.platforms) &&
                        formValues.platforms.length > 0) ||
                      (formValues.from && formValues.from !== '') ||
                      (formValues.to && formValues.to !== '') ||
                      formValues.scheduled ||
                      searchValue.trim() !== '';
                    return hasActiveFilters;
                  })()}
                  onReset={() => {
                    // Reset form values
                    filterForm.reset();
                    // Explicitly clear multi-select filter values
                    filterForm.setValue('statuses', []);
                    filterForm.setValue('channels', []);
                    filterForm.setValue('platforms', []);
                    filterForm.setValue('from', '');
                    filterForm.setValue('to', '');
                    filterForm.setValue('scheduled', false);
                    // Reset search value
                    setSearchValue('');
                    // Reset filtering states
                    setIsFiltering(false);
                    setHasAppliedFilters(false);
                    setCurrentFilterStatus('all');
                  }}
                />
                <Tabs
                  tabs={campaignsTabs as unknown as OptionInterface[]}
                  onClick={handleTabClick}
                  defaultValue={currentFilterStatus}
                />
              </>
            )}
          </div>
        </div>
        {((campaignsData?.total ?? 0) > 0 ||
          hasAppliedFilters ||
          isFiltering) && (
          <div className="bg-white">
            <div className="container mx-auto py-6 px-4 flex flex-col md:flex-row justify-between items-end md:items-center gap-4">
              <h3 className="text-2xl font-bold">
                {campaignsData?.total} campaigns
              </h3>
              <div>
                <div className="relative w-full max-w-md sm:max-w-lg md:max-w-xl">
                  <input
                    type="text"
                    className="w-full border-0 border-b border-gray-300 text-base sm:text-lg py-2 pr-10 focus:outline-none focus:ring-0 focus:border-gray-400 bg-transparent placeholder:text-gray-400"
                    placeholder="Search"
                    value={searchValue}
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                      if (e.target.value.length > 0) {
                        setIsFiltering(true);
                      }
                    }}
                    style={{ fontWeight: 400 }}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                    aria-label="Search"
                  >
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                      <circle
                        cx="11"
                        cy="11"
                        r="8"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        d="M21 21l-3.5-3.5"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="container mx-auto py-6 px-4 ">
          {/* Show original "No campaigns" state when no campaigns exist and no filtering */}
          {(!campaignsData || campaignsData.total === 0) &&
            !hasAppliedFilters &&
            !isFiltering && (
              <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-40">
                <NoCampaignIllustration />
                <p className="font-bold">No campaigns</p>
                <p className="text-sm text-base-coolGray text-center">
                  You don't have any campaigns added yet.
                  <br /> Create your first campaign
                </p>
                {can('createCampaigns') && (
                  <Button
                    onClick={handleCreateScheduleModal}
                    leftNode={<CampaignsIcon className="[&_path]:stroke-white" />}
                  >
                    Schedule new campaign
                  </Button>
                )}
              </div>
            )}

          {/* Show filtered empty states when filtering is active and no results */}
          {campaignsData &&
            campaignsData.total === 0 &&
            (hasAppliedFilters || isFiltering) &&
            (() => {
              const emptyState = getEmptyStateConfig();
              const ImageComponent = emptyState.image;

              return (
                <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-40">
                  {typeof ImageComponent === 'string' ? (
                    <img
                      src={ImageComponent}
                      alt="No campaigns"
                      className="w-32 h-32"
                    />
                  ) : (
                    <ImageComponent />
                  )}
                  <p className="font-bold">{emptyState.header}</p>
                  <p className="text-sm text-base-coolGray text-center whitespace-pre-line">
                    {emptyState.description}
                  </p>
                  {can('createCampaigns') &&
                  ((currentFilterStatus !== 'cancelled' &&
                    currentFilterStatus !== 'draft') ||
                  hasOtherFilters(filterForm.getValues())) ? (
                    <Button onClick={handleCreateScheduleModal} kind="primary">
                      Schedule a campaign
                    </Button>
                  ) : null}
                </div>
              );
            })()}
          {campaignsData && (
            <div className="bg-white overflow-auto">
              <GenericTable
                data={campaignsData?.campaigns ?? []}
                currentPage={page}
                headers={[
                  { label: 'Campaigns', field: 'name', sortable: true },
                  { label: 'Status', field: 'status', sortable: true },
                  { label: 'Follow up', field: 'followUpDate', sortable: true },
                  { label: 'Platforms', field: 'platforms', sortable: true },
                  {
                    label: 'Customers',
                    field: 'customerCount',
                    sortable: true,
                  },
                ]}
                renderRow={(data) =>
                  data.map((row) => (
                    <CampaignsTableRow
                      key={row.id}
                      data={row}
                      onClick={() => handleCampaignClick(row.id, row.status)}
                      refetch={refetch}
                      onDelete={handleDeleteModal}
                      isHighlighted={highlightedCampaignId === row.id}
                      onHighlight={handleHighlightCampaign}
                      disabledActions={{
                        deleteCampaign: !can('cancelCampaigns'),
                        cancelCampaign: !can('cancelCampaigns'),
                        cloneCampaign: !can('createCampaigns'),
                      }}
                    />
                  ))
                }
                isLoading={false}
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={handleSortingTable}
                pagination={{
                  total: campaignsData?.total ?? 0,
                  handleNextPage: handleNextPage,
                  handlePrevPage: handlePreviousPage,
                  handleLimitChange: handleLimitChange,
                }}
              />
            </div>
          )}
        </div>
      </div>
      <BaseDialog
        size="max-w-2xl"
        open={Boolean(deleteModal)}
        onClose={() => handleDeleteModal()}
      >
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <BinIcon width={24} height={24} />
          </div>
          <div className="mr-auto my-auto">
            <h5 className="font-bold text-lg">Delete campaign</h5>
          </div>
          <div>
            <button type="button" onClick={() => handleDeleteModal()}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4">
          <p className="text-base-davyGray mb-4">
            Are you sure you want to delete this campaign? All data about it
            will be deleted from the database.
          </p>

          {deleteModal &&
            (() => {
              const scheduleSummary = getScheduleSummary(deleteModal);

              if (scheduleSummary && scheduleSummary.futureCount > 0) {
                const {
                  pastCount,
                  futureCount,
                  pastSchedules,
                  futureSchedules,
                } = scheduleSummary;

                return (
                  <div className="mb-4">
                    <p className="font-bold text-base-davyGray mb-3">
                      {pastCount > 0
                        ? `This campaign has been sent ${pastCount} time${pastCount > 1 ? 's' : ''}, and ${futureCount} more ${futureCount > 1 ? 'are' : 'is'} scheduled.`
                        : `This campaign has not been sent yet.`}
                    </p>

                    <div className="bg-gray-100 rounded-lg p-4 space-y-3">
                      {pastSchedules.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="bg-green-100 p-1 rounded-sm flex items-center justify-center">
                            <CalendarCheckIcon className="w-4 h-4" />
                          </div>
                          <span className="text-sm text-gray-700">
                            Sent on:
                          </span>
                          <span className="text-sm text-gray-700">
                            {pastSchedules
                              .map((schedule: any) =>
                                dayjs(schedule.start).format('MMM DD, YYYY')
                              )
                              .join(', ')}
                          </span>
                        </div>
                      )}

                      {futureSchedules.map((schedule: any, index: number) => (
                        <div
                          key={schedule.id}
                          className="flex items-center gap-2"
                        >
                          <div className="bg-gray-100 p-1 rounded-sm flex items-center justify-center">
                            <CalendarIcon className="w-4 h-4" />
                          </div>
                          <span className="text-sm text-gray-700">
                            {pastCount > 0
                              ? 'Send again:'
                              : index === 0
                                ? 'Initial send:'
                                : 'Send again:'}
                          </span>
                          <span className="text-sm text-gray-700">
                            {dayjs(schedule.start).format('MMM DD, YYYY')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              return null;
            })()}

          <div className="flex items-center gap-4 justify-center py-4">
            <Button
              kind="secondary"
              className="w-full"
              type="button"
              onClick={() => handleDeleteModal()}
            >
              Cancel
            </Button>
            <Button
              kind="danger"
              className="w-full"
              type="submit"
              onClick={() => deleteCampaignAction()}
            >
              Delete campaign
            </Button>
          </div>
        </div>
      </BaseDialog>
      <BaseDialog
        size="max-w-xl"
        open={createSecheduleModal}
        onClose={handleCreateScheduleModal}
      >
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <CampaignScheduleIcon width={24} height={24} />
          </div>
          <div className="mr-auto my-auto">
            <h5 className="font-bold text-lg">Create a new campaign</h5>
          </div>
          <div>
            <button type="button" onClick={() => handleCreateScheduleModal()}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4 space-y-4">
          <button
            type="button"
            onClick={() => createCampaign()}
            className="bg-base-seasalt rounded-lg flex p-4 gap-2 w-full"
          >
            <div className="bg-white rounded-[4px] flex items-center justify-center h-8 w-8">
              <PlaneIcon />
            </div>
            <div className="text-left">
              <p className="text-sm font-bold">Simple</p>
              <p className="text-base-coolGray text-sm">
                Create a one-off campaign that can be sent once
                <br /> or multiple times
              </p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => createAutomation()}
            className="bg-base-seasalt rounded-lg flex p-4 gap-2 w-full"
          >
            <div className="bg-white rounded-[4px] flex items-center justify-center h-8 w-8">
              <AutomationIcon />
            </div>
            <div className="text-left">
              <p className="text-sm font-bold">Automation</p>
              <p className="text-base-coolGray text-sm">
                Create conditions for automatic email or text
                <br /> dispatches.
              </p>
            </div>
          </button>
        </div>
      </BaseDialog>
    </>
  );
};
